import {
  regionOptions,
  sortOptions,
  statusFilterOptions,
  timeRangeOptions,
  typeOptions,
} from '@/utils/format';

export interface CategoryFilters {
  timeRange: string;
  region: string;
  type: string;
  status: string;
  sort: string;
}

interface FilterBarProps {
  filters: CategoryFilters;
  onChange: (patch: Partial<CategoryFilters>) => void;
  onReset: () => void;
  total: number;
  expanded: boolean;
  onToggle: () => void;
}

const SelectGroup = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (value: string) => void;
}) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-foreground-500 whitespace-nowrap">{label}</span>
    <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 overflow-x-auto">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
            value === option.id
              ? 'bg-background-50 text-primary-700 font-medium'
              : 'text-foreground-600 hover:text-foreground-900'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

export default function FilterBar({
  filters,
  onChange,
  onReset,
  total,
  expanded,
  onToggle,
}: FilterBarProps) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 lg:hidden">
        <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center text-primary-600" />
          筛选与排序
        </span>
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-1 text-xs text-primary-600"
        >
          {expanded ? '收起' : '展开'}
          <i className={`${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} w-4 h-4 flex items-center justify-center`} />
        </button>
      </div>

      <div className={`${expanded ? 'block' : 'hidden'} lg:block px-4 py-4 border-t lg:border-t-0 border-background-200`}>
        <div className="flex flex-col gap-4">
          <SelectGroup
            label="时间范围"
            value={filters.timeRange}
            options={timeRangeOptions}
            onChange={(value) => onChange({ timeRange: value })}
          />
          <SelectGroup
            label="赛事地区"
            value={filters.region}
            options={regionOptions}
            onChange={(value) => onChange({ region: value })}
          />
          <SelectGroup
            label="赛事类型"
            value={filters.type}
            options={typeOptions}
            onChange={(value) => onChange({ type: value })}
          />
          <SelectGroup
            label="赛事状态"
            value={filters.status}
            options={statusFilterOptions}
            onChange={(value) => onChange({ status: value })}
          />
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-background-200">
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序方式</span>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => onChange({ sort: option.id })}
                    className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                      filters.sort === option.id
                        ? 'bg-background-50 text-accent-700 font-medium'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-500">
                共 <strong className="text-primary-700">{total}</strong> 条结果
              </span>
              <button
                type="button"
                onClick={onReset}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line w-3.5 h-3.5 flex items-center justify-center" />
                重置筛选
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}